import { Zap, Flame, Thermometer, Scale, Info, Building2, Ban } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

/**
 * Shared renderers for the CEP energy block and the upward reconciliation.
 *
 * Used by BOTH Data Comparison and Consolidated Results, which read the same
 * `energy` / `reconciliation` payload from the backend. One renderer so the
 * same numbers cannot be labelled differently on two screens.
 *
 * Three rules this file exists to enforce in the UI:
 *
 *   1. EVERY VECTOR SHOWS ITS OWN UNIT. Electrical is kWh, gas is m3, thermal
 *      is modelled kWh. There is deliberately no "total energy" tile — summing
 *      them would need a calorific value the system has no source for, and
 *      would present a model output as metered consumption.
 *   2. AN ABSENT VECTOR RENDERS "not uploaded", NEVER 0. The backend sends null
 *      for a vector with no file, and a zero would read as "measured, and it
 *      was nothing".
 *   3. A BLOCKED REASON IS RENDERED AS A REASON. Where a number has no source
 *      (per-department energy, CO2 from metered energy) the UI shows why, not a
 *      dash and not a zero.
 */

/** Format a number for display, or an em dash when there is nothing to show. */
const fmt = (v, digits = 0) =>
  v === null || v === undefined
    ? "—"
    : v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const period = (p) => (p ? `${p.year}-${String(p.month).padStart(2, "0")}` : "—");

const periodRange = (range) =>
  range && range.first ? `${period(range.first)} → ${period(range.last)}` : "—";

/**
 * A backend reason object rendered in the user's language.
 *
 * Prefers the `reason_<code>` i18n entry and falls back to the message the
 * backend sent. `useT` returns the key itself when there is no entry, so a
 * reason code added to the backend later still renders readable English here
 * instead of a raw key.
 */
export const useReasonText = () => {
  const t = useT();
  return (reason) => {
    if (!reason) return null;
    const key = `reason_${reason.code}`;
    const translated = t(key);
    return translated === key ? reason.message : translated;
  };
};

export const BlockedNote = ({ reason, icon = true }) => {
  const reasonText = useReasonText();
  if (!reason) return null;
  return (
    <div className="blocked-note" data-testid={`blocked-${reason.code}`}>
      {icon && <Ban size={14} className="blocked-note-icon" />}
      <span>{reasonText(reason)}</span>
    </div>
  );
};

const VectorTile = ({ icon, label, vector, testId, digits = 0 }) => {
  const t = useT();
  return (
    <div className="cep-tile" data-testid={testId}>
      <div className="cep-tile-head">
        {icon}
        <span className="cep-tile-label">{label}</span>
      </div>
      {vector ? (
        <>
          <div className="cep-tile-value">
            {fmt(vector.total, digits)} <small>{vector.unit}</small>
          </div>
          <div className="cep-tile-meta">
            {vector.n_months} {t("cep_months")} · {periodRange({ first: vector.first, last: vector.last })}
          </div>
          {vector.kw_demand_peak !== undefined && vector.kw_demand_peak !== null && (
            <div className="cep-tile-meta">
              {t("cep_peak_demand")}: {fmt(vector.kw_demand_peak, 1)} kW
            </div>
          )}
        </>
      ) : (
        <>
          <div className="cep-tile-value cep-tile-absent">{t("cep_not_uploaded")}</div>
          <div className="cep-tile-meta">{t("cep_vector_absent")}</div>
        </>
      )}
    </div>
  );
};

/** The CEP energy block: per-vector totals, coverage, and the withheld CO2. */
export const CepEnergyPanel = ({ energy }) => {
  const t = useT();
  if (!energy) return null;

  const coverage = energy.coverage || {};
  const missing = coverage.months_missing_electrical || [];
  const hasData = (coverage.n_months || 0) > 0;

  return (
    <div className="card mb-lg" data-testid="cep-energy-panel">
      <h4 className="card-title">{t("cep_title")}</h4>
      <p className="card-subtitle">{t("cep_subtitle")}</p>

      {!hasData ? (
        <div className="cep-empty" data-testid="cep-empty">
          <Info size={18} />
          <span>{t("cep_no_data")}</span>
        </div>
      ) : (
        <>
          <div className="cep-tiles">
            <VectorTile
              icon={<Zap size={18} className="cep-icon-electrical" />}
              label={t("cep_electrical")}
              vector={energy.electrical}
              testId="cep-tile-electrical"
            />
            <VectorTile
              icon={<Flame size={18} className="cep-icon-gas" />}
              label={t("cep_gas")}
              vector={energy.gas}
              testId="cep-tile-gas"
            />
            <VectorTile
              icon={<Thermometer size={18} className="cep-icon-thermal" />}
              label={t("cep_thermal")}
              vector={energy.thermal}
              testId="cep-tile-thermal"
            />
          </div>

          <div className="cep-coverage" data-testid="cep-coverage">
            <span>
              <strong>{t("cep_coverage")}:</strong> {coverage.n_months} {t("cep_months")} ·{" "}
              {periodRange(coverage)}
            </span>
            {missing.length > 0 && (
              <span className="badge badge-warning" data-testid="cep-missing-electrical">
                {missing.length} {t("cep_missing_electrical_months")}
              </span>
            )}
          </div>

          {/* By-building breakdown, client-wide reads only. A multi-building
              client collapsed into one number hides which building the energy
              is in. */}
          {energy.by_asset && energy.by_asset.length > 1 && (
            <div className="table-container mt-md">
              <div className="cep-subhead">
                <Building2 size={14} /> {t("cep_by_building")}
              </div>
              <table className="table" data-testid="cep-by-asset-table">
                <thead>
                  <tr>
                    <th>{t("scope_building")}</th>
                    <th>{t("cep_electrical")}</th>
                    <th>{t("cep_gas")}</th>
                    <th>{t("cep_thermal")}</th>
                    <th>{t("cep_coverage")}</th>
                  </tr>
                </thead>
                <tbody>
                  {energy.by_asset.map((a) => (
                    <tr key={a.asset_uuid} data-testid={`cep-asset-row-${a.asset_uuid}`}>
                      <td>
                        <strong>{a.building_name || a.asset_uuid}</strong>
                      </td>
                      <td>{a.electrical ? `${fmt(a.electrical.total)} kWh` : t("cep_not_uploaded")}</td>
                      <td>{a.gas ? `${fmt(a.gas.total)} m³` : t("cep_not_uploaded")}</td>
                      <td>{a.thermal ? `${fmt(a.thermal.total)} kWh` : t("cep_not_uploaded")}</td>
                      <td>
                        {a.coverage.n_months} {t("cep_months")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* No CO2 is derived from metered energy — the factor is unresolved. */}
      <div className="cep-withheld" data-testid="cep-co2-withheld">
        <span className="cep-withheld-label">{t("cep_co2_withheld")}</span>
        <BlockedNote reason={energy.co2_blocked} />
      </div>

      {(energy.blocked || []).map((b) => (
        <BlockedNote key={b.code} reason={b} />
      ))}
    </div>
  );
};

const ComparisonRow = ({ comparison }) => {
  const t = useT();
  const labelKey =
    comparison.key === "inventory_vs_cep" ? "recon_inventory_vs_cep" : "recon_iot_vs_cep";

  return (
    <div
      className={`recon-row ${comparison.is_comparable ? "" : "recon-row-uncomparable"}`}
      data-testid={`recon-${comparison.key}`}
    >
      <div className="recon-row-head">
        <span className="recon-row-label">{t(labelKey)}</span>
        {/* The verdict is withheld, not the arithmetic. This badge is the
            whole reason the numbers beside it are safe to show. */}
        {!comparison.is_comparable && (
          <span className="badge badge-muted" data-testid={`recon-${comparison.key}-uncomparable`}>
            {t("recon_not_comparable")}
          </span>
        )}
      </div>
      <div className="recon-figures">
        <div className="recon-figure">
          <span className="recon-figure-label">{t("recon_bottom_up")}</span>
          <span className="recon-figure-value">{fmt(comparison.bottom_up_kwh)} <small>kWh</small></span>
        </div>
        <div className="recon-figure">
          <span className="recon-figure-label">{t("recon_reference")}</span>
          <span className="recon-figure-value">
            {comparison.reference_kwh === null ? "—" : fmt(comparison.reference_kwh)}{" "}
            <small>kWh</small>
          </span>
        </div>
        <div className="recon-figure">
          <span className="recon-figure-label">{t("recon_difference")}</span>
          <span className="recon-figure-value">
            {comparison.difference_kwh === null
              ? "—"
              : `${comparison.difference_kwh > 0 ? "+" : ""}${fmt(comparison.difference_kwh)}`}{" "}
            <small>kWh</small>
          </span>
        </div>
        <div className="recon-figure">
          <span className="recon-figure-label">{t("recon_ratio")}</span>
          <span className="recon-figure-value">
            {comparison.ratio === null ? "—" : `${(comparison.ratio * 100).toFixed(1)}%`}
          </span>
        </div>
      </div>
      {!comparison.is_comparable && (
        <div className="recon-caveat-note">{t("recon_not_comparable_note")}</div>
      )}
      {(comparison.caveats || []).map((c) => (
        <BlockedNote key={c.code} reason={c} />
      ))}
    </div>
  );
};

/** The upward reconciliation: departments summed vs the building's CEP total. */
export const ReconciliationPanel = ({ reconciliation }) => {
  const t = useT();
  if (!reconciliation) return null;

  const ref = reconciliation.reference || {};
  const bottomUp = reconciliation.bottom_up || {};

  return (
    <div className="card mb-lg" data-testid="reconciliation-panel">
      <h4 className="card-title">
        <Scale size={16} /> {t("recon_title")}
      </h4>
      <p className="card-subtitle">{t("recon_subtitle")}</p>

      <div className="recon-header" data-testid="recon-header">
        <span>
          <strong>{t("recon_period")}:</strong> {periodRange(ref.period)}
          {ref.n_months ? ` (${ref.n_months} ${t("cep_months")})` : ""}
        </span>
        <span>
          <strong>{bottomUp.n_departments || 0}</strong> {t("recon_depts_counted")}
        </span>
      </div>

      {ref.cep_electrical_kwh === null && (
        <div className="cep-empty" data-testid="recon-no-reference">
          <Info size={18} />
          <span>{t("recon_no_reference")}</span>
        </div>
      )}

      {(reconciliation.comparisons || []).map((c) => (
        <ComparisonRow key={c.key} comparison={c} />
      ))}

      {/* Downward attribution has no source. Rendered, never computed. */}
      <BlockedNote reason={reconciliation.allocation_blocked} />
    </div>
  );
};

/**
 * Departments dropped from the comparison table but still counted in the
 * reconciliation. Named explicitly so asset-scoped data never disappears
 * behind a client-wide N/A flag.
 */
export const ExcludedDepartmentsPanel = ({ excluded }) => {
  const t = useT();
  if (!excluded || excluded.length === 0) return null;

  return (
    <div className="card mb-lg" data-testid="excluded-departments-panel">
      <h4 className="card-title">{t("excluded_title")}</h4>
      <p className="card-subtitle">{t("excluded_subtitle")}</p>
      <div className="table-container">
        <table className="table" data-testid="excluded-departments-table">
          <thead>
            <tr>
              <th>{t("department")}</th>
              <th>{t("inv_energy")}</th>
              <th>{t("iot_energy")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {excluded.map((d) => (
              <tr key={d.department_key} data-testid={`excluded-dept-${d.department_key}`}>
                <td>
                  <strong>{d.department}</strong>{" "}
                  <span className="badge badge-na">N/A</span>
                </td>
                <td>{fmt(d.inventory_energy_kwh, 2)}</td>
                <td>{fmt(d.iot_energy_total_kwh, 2)}</td>
                <td>
                  {d.has_asset_scoped_data ? (
                    <span className="badge badge-warning">{t("excluded_has_data")}</span>
                  ) : (
                    <span className="badge badge-neutral">{t("excluded_no_data")}</span>
                  )}
                  {d.included_in_bottom_up && (
                    <span className="badge badge-info ml-xs">{t("excluded_counted")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="recon-caveat-note">{t("na_scope_note")}</div>
    </div>
  );
};
